import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Lock, Mail, AlertTriangle, Eye, EyeOff, LogIn } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Allowed emails that can access the emergency login (super admins only)
const ALLOWED_EMERGENCY_EMAILS = [
  "jason@jci-marketing.com",
  "kevin@jci-marketing.com"
];

export default function EmergencyAccessPage() {
  const navigate = useNavigate();
  
  // Stage 1: Identity verification via Emergent Auth (same as normal login)
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [authError, setAuthError] = useState("");
  
  // Stage 2: Master password login
  const [targetEmail, setTargetEmail] = useState("");
  const [masterPassword, setMasterPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // On mount, clear any existing auth to ensure clean state
  useEffect(() => {
    // Clear all auth data - this is a backdoor, not normal login
    localStorage.removeItem("session_token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("session_token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("viewing_tenant_id");
    sessionStorage.removeItem("viewing_tenant_name");
  }, []);

  // Check URL params for verification callback or errors
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.replace("#", ""));
    
    // Check for errors
    const error = urlParams.get("error");
    if (error) {
      const errorMessages = {
        "oauth_denied": "OAuth authorization was denied",
        "not_authorized": "Your account is not authorized for emergency access",
        "verification_failed": "Verification failed - please try again",
        "invalid_session": "Invalid session - please try again"
      };
      setAuthError(errorMessages[error] || error);
    }
    
    // Check for already verified state (from backend redirect)
    const verified = urlParams.get("verified");
    const email = urlParams.get("email");
    const token = urlParams.get("token");
    
    if (verified === "true" && email && token && ALLOWED_EMERGENCY_EMAILS.includes(email.toLowerCase())) {
      setIsVerified(true);
      setVerifiedEmail(email);
      setVerificationToken(token);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Check for Emergent Auth callback (session_id in hash)
    const sessionId = hashParams.get("session_id");
    if (sessionId) {
      handleEmergentAuthCallback(sessionId);
    }
  }, []);

  // Handle Emergent Auth callback - verify identity only, don't create session
  const handleEmergentAuthCallback = async (sessionId) => {
    setVerifying(true);
    setAuthError("");
    
    try {
      // Call backend to verify the session and check if email is allowed
      const response = await axios.post(`${API}/auth/emergency-verify-identity`, {
        session_id: sessionId
      });
      
      if (response.data.verified && response.data.email) {
        const email = response.data.email.toLowerCase();
        
        if (ALLOWED_EMERGENCY_EMAILS.includes(email)) {
          setIsVerified(true);
          setVerifiedEmail(email);
          setVerificationToken(response.data.verification_token);
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setAuthError("Your account is not authorized for emergency access");
        }
      } else {
        setAuthError("Verification failed - please try again");
      }
    } catch (error) {
      console.error("Emergency verification failed:", error);
      setAuthError(error.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  // Initiate verification using Emergent Auth (same OAuth that works for regular login)
  const handleVerifyIdentity = () => {
    setVerifying(true);
    setAuthError("");
    
    // Use Emergent Auth - redirect back to THIS page (not the normal callback)
    // We'll handle the session_id ourselves to verify identity without creating a session
    const currentUrl = window.location.origin + window.location.pathname;
    const redirectUrl = currentUrl; // Redirect back to emergency page
    
    // Redirect to Emergent Auth
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Handle master password login (Stage 2)
  const handleMasterLogin = async (e) => {
    e.preventDefault();
    
    if (!targetEmail || !masterPassword) {
      toast.error("Email and master password required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/emergency-master-login`,
        {
          email: targetEmail,
          master_password: masterPassword,
          verified_by: verifiedEmail,
          verification_token: verificationToken
        }
      );

      // Clear any existing session data first
      localStorage.removeItem("session_token");
      localStorage.removeItem("user");
      sessionStorage.clear();

      // Store NEW session for TARGET user
      localStorage.setItem("session_token", response.data.session_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Store tenant info for target user
      if (response.data.user?.tenant_memberships?.length > 0) {
        const firstTenant = response.data.user.tenant_memberships[0];
        sessionStorage.setItem("viewing_tenant_id", firstTenant.tenant_id);
        sessionStorage.setItem("viewing_tenant_name", firstTenant.tenant_name || "");
      }

      toast.success(`Emergency access granted to ${targetEmail}`);
      
      // Force full page reload to clear any cached state
      window.location.href = "/app/dashboard";
    } catch (error) {
      console.error("Emergency login failed:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Stage 1: Verify identity with Emergent Auth (uses the same working OAuth)
  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900/90 border-slate-700 shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
              <Shield className="w-8 h-8 text-amber-400" />
            </div>
            <CardTitle className="text-2xl text-white">Identity Verification Required</CardTitle>
            <CardDescription className="text-slate-400">
              You must verify your identity before accessing emergency controls.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {authError && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-200">
                    <p className="font-medium">{authError}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-200">
                  <p className="font-medium mb-1">Restricted Access</p>
                  <p className="text-amber-300/80">
                    Only authorized administrators can access this page. 
                    Your identity will be verified via Google sign-in.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleVerifyIdentity}
              disabled={verifying}
              className="w-full bg-white hover:bg-slate-100 text-slate-900 font-medium py-6"
              data-testid="verify-google-btn"
            >
              {verifying ? (
                "Redirecting..."
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Verify with Google
                </>
              )}
            </Button>

            <p className="text-xs text-slate-500 text-center">
              All verification attempts are logged and audited.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Stage 2: Master password login (only shown after identity verification)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/20 to-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900/90 border-red-500/30 shadow-2xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <CardTitle className="text-2xl text-white">Emergency Access</CardTitle>
          <CardDescription className="text-slate-400">
            Verified as: <span className="text-green-400 font-medium">{verifiedEmail}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleMasterLogin} className="space-y-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">
                  This grants full access to the target account. All actions are logged.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetEmail" className="text-slate-300">
                <Mail className="w-4 h-4 inline mr-2" />
                Target Account Email
              </Label>
              <Input
                id="targetEmail"
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                placeholder="user@example.com"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                required
                data-testid="target-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="masterPassword" className="text-slate-300">
                <Lock className="w-4 h-4 inline mr-2" />
                Master Password
              </Label>
              <div className="relative">
                <Input
                  id="masterPassword"
                  type={showPassword ? "text" : "password"}
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                  placeholder="Enter master password"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 pr-10"
                  required
                  data-testid="master-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-5"
              data-testid="access-account-btn"
            >
              {loading ? (
                "Authenticating..."
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Access Account
                </>
              )}
            </Button>

            <p className="text-xs text-slate-500 text-center mt-4">
              Session will be logged as emergency access by {verifiedEmail}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
